import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { getTodayDate } from '../utils/dateUtils';

interface PomodoroSettings {
  workDuration: number; // in minutes
  breakDuration: number; // in minutes
  autoStartBreaks: boolean;
  autoStartPomodoros: boolean;
  soundEnabled: boolean;
  notificationEnabled: boolean;
}

interface CurrentSession {
  phase: 'work' | 'break' | null;
  timerRemaining: number; // in seconds
  isRunning: boolean;
  hasStarted: boolean;
  completedSessions: number;
  todayWorkMinutes: number;
  lastPhase: 'work' | 'break' | null;
  lastWorkMinutes: number;
}

interface PomodoroState {
  // Settings
  pomodoroSettings: PomodoroSettings;
  
  // Current Session
  currentSession: CurrentSession;
  
  // Log
  pomodoroLog: Record<string, number>; // date -> minutes
  
  // Actions
  updatePomodoroSettings: (settings: Partial<PomodoroSettings>) => void;
  startTimer: () => void;
  stopTimer: () => void;
  resetTimer: () => void;
  switchPhase: () => void;
  completeSession: () => void;
  updateTimerRemaining: (remaining: number) => void;
  updatePomodoroLog: (minutes: number) => void;
}

const defaultPomodoroSettings: PomodoroSettings = {
  workDuration: 25,
  breakDuration: 5,
  autoStartBreaks: true,
  autoStartPomodoros: true,
  soundEnabled: true,
  notificationEnabled: true,
};

export const usePomodoroStore = create<PomodoroState>()(
  persist(
    (set, get) => ({
      // Initial state
      pomodoroSettings: defaultPomodoroSettings,
      currentSession: {
        phase: null,
        timerRemaining: defaultPomodoroSettings.workDuration * 60,
        isRunning: false,
        hasStarted: false,
        completedSessions: 0,
        todayWorkMinutes: 0, // もう使わないので0
        lastPhase: null,
        lastWorkMinutes: 0,
      },
      pomodoroLog: {},

      // Actions
      updatePomodoroSettings: (settings) =>
        set((state) => ({
          pomodoroSettings: { ...state.pomodoroSettings, ...settings },
        })),

      startTimer: () => {
        const { currentSession, pomodoroSettings } = get();
        
        // Set initial phase if not started
        if (!currentSession.hasStarted || currentSession.phase === null) {
          set({
            currentSession: {
              ...currentSession,
              phase: 'work',
              isRunning: true,
              hasStarted: true,
              timerRemaining: currentSession.timerRemaining > 0 
                ? currentSession.timerRemaining 
                : pomodoroSettings.workDuration * 60,
            },
          });
        } else {
          // Resume existing timer
          set((state) => ({
            currentSession: {
              ...state.currentSession,
              isRunning: true,
            },
          }));
        }
      },

      stopTimer: () =>
        set((state) => ({
          currentSession: {
            ...state.currentSession,
            isRunning: false,
          },
        })),

      resetTimer: () => {
        const { pomodoroSettings } = get();
        set({
          currentSession: {
            phase: null,
            timerRemaining: pomodoroSettings.workDuration * 60,
            isRunning: false,
            hasStarted: false,
            completedSessions: 0,
            todayWorkMinutes: 0,
            lastPhase: null,
            lastWorkMinutes: 0,
          },
        });
      },

      switchPhase: () => {
        const { currentSession, pomodoroSettings } = get();
        const newPhase = currentSession.phase === 'work' ? 'break' : 'work';
        const duration = newPhase === 'work' 
          ? pomodoroSettings.workDuration 
          : pomodoroSettings.breakDuration;
        
        set({
          currentSession: {
            ...currentSession,
            phase: newPhase,
            timerRemaining: duration * 60,
            isRunning: false, // 自動開始はタイマーフック側で処理
            lastPhase: currentSession.phase,
          },
        });
      },

      completeSession: () => {
        const { currentSession, pomodoroSettings } = get();
        
        if (currentSession.phase === 'work') {
          // 作業セッション完了時
          const workMinutes = pomodoroSettings.workDuration;
          
          // 今日の日付を取得
          const dateKey = getTodayDate();
          
          set((state) => ({
            currentSession: {
              ...state.currentSession,
              completedSessions: state.currentSession.completedSessions + 1,
              lastWorkMinutes: workMinutes,
            },
            pomodoroLog: {
              ...state.pomodoroLog,
              [dateKey]: (state.pomodoroLog[dateKey] || 0) + workMinutes,
            },
          }));
        }
      },

      updateTimerRemaining: (remaining) =>
        set((state) => ({
          currentSession: {
            ...state.currentSession,
            timerRemaining: remaining,
          },
        })),

      updatePomodoroLog: (minutes) => {
        const dateKey = getTodayDate();
        
        set((state) => ({
          pomodoroLog: {
            ...state.pomodoroLog,
            [dateKey]: (state.pomodoroLog[dateKey] || 0) + minutes,
          },
        }));
      },
    }),
    {
      name: 'sorarium-pomodoro-storage',
      partialize: (state) => ({
        pomodoroSettings: state.pomodoroSettings,
        pomodoroLog: state.pomodoroLog,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          // Reset timer with correct duration when state rehydrates
          const duration = state.pomodoroSettings.workDuration * 60;
          state.currentSession = {
            phase: null,
            timerRemaining: duration,
            isRunning: false,
            hasStarted: false,
            completedSessions: 0,
            todayWorkMinutes: 0,
            lastPhase: null,
            lastWorkMinutes: 0,
          };
        }
      },
    }
  )
);